import { Dialog, DialogContent, Button, Stack, Typography, TextField } from '@mui/material';
import DialogActionBar from './DialogActionBar';
import { OperationTeam } from 'src/networkRequest/operationsService';
import { useEffect, useState } from 'react';

interface AddTeamModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (name: string) => Promise<void>;
  operationTeams: OperationTeam[];
  onValidationError?: (errors: string[]) => void;
  loading?: boolean;
}

export default function AddTeamModal({
  open,
  onClose,
  onSave,
  operationTeams,
  onValidationError,
  loading = false
}: AddTeamModalProps) {

  const [ name, setName ] = useState<string>('');

  useEffect(
    () => {
      if (open) {
        clearFields();
      }
    },
    [ open ]
  );

  const clearFields = () => {
    setName('');
  };

  const getValidationErrors = (): string[] => {
    const validationErrors: string[] = [];
    if (name.length === 0) {
      validationErrors.push('Name cannot be empty');
    }
    const teamNames = operationTeams.map(ot => ot.name);
    if (teamNames.includes(name)) {
      validationErrors.push('A team with the same name already exists');
    }
    return validationErrors;
  };

  const isFormValid = () => {
    return (getValidationErrors().length === 0) 
  };

  const handleSave = async () => {
    const validationErrors = getValidationErrors();
    if (validationErrors.length > 0) {
      if (onValidationError) {
        onValidationError(validationErrors);
      }
      return;
    }
    
    // If validation passes, call the parent save handler
    try {
      await onSave(name);
    }
    catch (error) {
      // Error handling is delegated to the parent component
      console.error('Save failed:', error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 2,
          width: '600px',
          maxWidth: '600px',
          height: 'auto',
          maxHeight: '90vh',
          pt: 4,
          px: 3,
          pb: 2
        },
      }}
    >
      <DialogContent sx={{ p: 0, mb: 3 }}>
        <Stack spacing={4}>
          <Stack spacing={2}>
            <Typography variant="h5">
              Add Team
            </Typography>
          </Stack>          
          <Stack
            sx={{
              p: 2,
              backgroundColor: 'common/white_states/main',
              borderRadius: '10px',
              width: '100%',
              maxWidth: 832
            }}
          >
            <Stack spacing={2}>
              <TextField
                label="Team Name"
                value={name}
                onChange={(e) => { setName(e.target.value); }}
                variant="outlined"
                size="medium"
                fullWidth
                sx={{ maxWidth: 600 }}
              />
            </Stack>
          </Stack>
        </Stack>

      </DialogContent>

      <DialogActionBar sx={{ paddingRight: 2 }}>
        <Button onClick={onClose} variant="outlined" size="medium">
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          size="medium"
          disabled={loading || !isFormValid()}
        >
          {loading ? 'Adding...' : 'Add Team'}
        </Button>
      </DialogActionBar>
    </Dialog>
  );
}